import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Environment, EnvironmentService } from '@galaxy/core';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { SalesTeamService, SalespersonService } from '@vendasta/sales';
import {
  ListPostsRequestDateRangeFilterInterface,
  ListPostsResponseInterface,
  PostInterface,
  PostType,
  RenderOption,
  TheLoopService,
} from '@vendasta/the-loop';
import { ListPostsRequestInterface } from '@vendasta/the-loop/lib/_internal/interfaces';
import {
  DateFilterOptions,
  DateRangeSelectorService,
  LocalStorageService,
  SavedFilter,
  URLParamService,
} from '@vendasta/uikit';
import moment from 'moment';
import { BehaviorSubject, Observable, Subscription, combineLatest, of } from 'rxjs';
import { catchError, finalize, map, publishReplay, refCount, skipWhile, switchMap, take, tap } from 'rxjs/operators';
import {
  LoggedInUserInfoServiceInterface,
  LoggedInUserInfoServiceInterfaceToken,
} from '../../interfaces/logged-in-user-info.service.interface';
import { Market } from '../../interfaces/market.interface';

const THE_LOOP = 'the-loop-filters-';
export const MAIN_FILTER = '__main_filter__';

const PAGE_SIZE = 25;
export const ALL_CHANNELS_OPTION = { name: 'FRONTEND.LOOP.ALL_CHANNELS', value: '' };

// Any value will always be filtered on and the associated selector will be hidden
export interface ForcedFilterOptions {
  marketIds?: string[];
  userId?: string;
}

export interface LoopOptions {
  forcedFilters?: ForcedFilterOptions;
  forcedChannel?: string;
  defaultChannel?: string;
  compact?: boolean;
  pageSize?: number;
  // The id of this specific instance of the loop so the filters aren't cross contaminated with other instances of the loop
  instanceId: string;
}

export interface DisplayFilter {
  filterId: string;
  filterTypeTranslateKey?: string;
  displayName: string;
  displayName$?: Observable<string>;
  icon?: string;
}

export interface FilterOption {
  name: string;
  value: string;
}

export interface FilterConfig {
  id: string;
  typeId: string;
  title: string;
  options: FilterOption[];
}

export interface ArmApiResponse<T> {
  version: string;
  data: T;
  requestId: string;
  responseTime: number;
  statusCode: number;
}

export interface DigitalAgentApiInterface {
  digital_agent_id: string;
  partner_id: string;
  first_name: string;
  last_name: string;
  email: string;
  created: Date;
  updated: Date;
  _accessible_partner_ids: string[];
  partner_permissions: object;
  _user_id: string;
}

interface ResourceFilterSelectedValues {
  id: string;
  values: string[];
}

interface AppliedFilters {
  market: FilterOption;
  salespeopleUserIds: string[];
  digitalagentsUserIds: string[];
  salesTeam: FilterOption;
  resourceFilters: ResourceFilterSelectedValues[];
  dateRangeKey: DateFilterOptions;
  dateRange: ListPostsRequestDateRangeFilterInterface;
  channel: string;
}

@Injectable()
export class LoopV2FiltersService implements OnDestroy {
  private posts$$: BehaviorSubject<PostInterface[]> = new BehaviorSubject<PostInterface[]>([]);
  posts$: Observable<PostInterface[]> = this.posts$$.asObservable();

  private loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  loading$: Observable<boolean> = this.loading$$.asObservable();

  private hasMore$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  hasMore$: Observable<boolean> = this.hasMore$$.asObservable();

  private readonly selectedMarket$$ = new BehaviorSubject<FilterOption>(null);
  public readonly selectedMarket$ = this.selectedMarket$$.asObservable();
  public readonly marketOptions$: Observable<FilterOption[]>;
  public readonly marketFilterDisplay$: Observable<string>;

  private readonly selectedSalespeopleUserIds$$ = new BehaviorSubject<string[]>([]);
  private readonly selectedDigitalAgentsUserIds$$ = new BehaviorSubject<string[]>([]);
  public readonly selectedSalespeopleUserIds$ = this.selectedSalespeopleUserIds$$.asObservable();
  public readonly selectedDigitalAgentsUserIds$ = this.selectedDigitalAgentsUserIds$$.asObservable();
  public readonly salespeopleOptions$: Observable<FilterOption[]>;
  public readonly digitalagentsOptions$: Observable<FilterOption[]>;
  public readonly salespeopleFilterDisplay$: Observable<string>;
  public readonly digitalagentsFilterDisplay$: Observable<string>;

  private readonly selectedSalesTeam$$ = new BehaviorSubject<FilterOption>(null);
  public readonly selectedSalesTeam$ = this.selectedSalesTeam$$.asObservable();
  public readonly salesTeamOptions$: Observable<FilterOption[]>;
  public readonly salesTeamFilterDisplay$: Observable<string>;

  private selectedDateRangeKey: DateFilterOptions;
  private readonly selectedDateRange$$ = new BehaviorSubject<ListPostsRequestDateRangeFilterInterface>(null);
  public readonly selectedDateRange$ = this.selectedDateRange$$.asObservable();
  public readonly dateRangeFilterDisplay$: Observable<string>;

  public readonly selectedSalesTeamMemberUserIds$: Observable<string[]>;

  private readonly resourceFiltersMap$$ = new BehaviorSubject<Map<string, FilterConfig>>(null);
  public readonly resourceFiltersMap$ = this.resourceFiltersMap$$.asObservable().pipe(skipWhile((f) => f === null));
  public readonly resourceFilters$: Observable<FilterConfig[]>;
  private readonly resourceFiltersSelectedValues = new Map<string, BehaviorSubject<ResourceFilterSelectedValues>>();

  private loopOptions$$ = new BehaviorSubject<LoopOptions>(null);
  private readonly loopOptions$ = this.loopOptions$$.asObservable().pipe(skipWhile((f) => f === null));
  private compact = false;
  private pageSize = PAGE_SIZE;
  private id: string;

  public readonly channelOptions$: Observable<FilterOption[]>;
  private selectedChannel$$ = new BehaviorSubject<string>(null);
  public readonly selectedChannel$ = this.selectedChannel$$.asObservable().pipe(
    skipWhile((f) => f === null),
    publishReplay(1),
    refCount(),
  );

  private postTypes$$ = new BehaviorSubject<PostType[]>(null);
  private postTypes$ = this.postTypes$$.asObservable().pipe(
    skipWhile((f) => f === null),
    publishReplay(1),
    refCount(),
  );

  private filteringMarketIds$: Observable<string[]>;
  private filteringUserIds$: Observable<string[]>;

  private nextCursor = '';

  subscriptions: Subscription[] = [];

  private savedFilters$$ = new BehaviorSubject<SavedFilter[]>([]);
  savedFilters$: Observable<SavedFilter[]>;

  constructor(
    private readonly loopService: TheLoopService,
    private readonly alertService: SnackbarService,
    private readonly translate: TranslateService,
    @Inject('partnerId$') private readonly partnerId$: Observable<string>,
    @Inject('markets$') private readonly markets$: Observable<Market[]>,
    private readonly salesTeamService: SalesTeamService,
    private readonly salespeopleService: SalespersonService,
    @Inject(LoggedInUserInfoServiceInterfaceToken)
    private readonly loggedInUserInfoService: LoggedInUserInfoServiceInterface,
    private dateRangeService: DateRangeSelectorService,
    private urlParamService: URLParamService,
    private localStorageService: LocalStorageService,
    private route: ActivatedRoute,
    private http: HttpClient,
    private environmentService: EnvironmentService,
  ) {
    this.loopOptions$
      .pipe(
        tap((loopOptions) => this.selectedChannel$$.next(loopOptions?.defaultChannel || '')),
        take(1),
      )
      .subscribe();

    this.marketOptions$ = this.markets$.pipe(
      take(1),
      map((markets) =>
        markets.map((market) => ({
          name: market.name,
          value: market.marketId,
        })),
      ),
      publishReplay(1),
      refCount(),
    );

    this.marketFilterDisplay$ = combineLatest([this.marketOptions$, this.selectedMarket$]).pipe(
      map(([marketOptions, selectedMarket]) => {
        if (!selectedMarket) {
          return '';
        }
        const market = marketOptions.find((m) => selectedMarket.value === m.value);
        return market.name;
      }),
    );

    this.filteringMarketIds$ = combineLatest([this.markets$, this.selectedMarket$, this.loopOptions$]).pipe(
      map(([markets, selectedMarket, loopOptions]) => {
        let marketIds;
        if (loopOptions?.forcedFilters?.marketIds) {
          marketIds = loopOptions?.forcedFilters.marketIds;
        } else if (selectedMarket) {
          marketIds = [selectedMarket.value];
        } else {
          marketIds = markets.map((m) => m.marketId);
        }
        return marketIds;
      }),
    );

    this.salespeopleOptions$ = this.loggedInUserInfoService.loggedInUserInfo$.pipe(
      take(1),
      switchMap((userInfo) => {
        return this.salespeopleService.listSalespersons(
          {
            partnerId: userInfo.partnerId,
            marketId: userInfo.marketId,
          },
          '',
          1000,
        );
      }),
      map((resp) =>
        resp.results?.map((sp) => ({
          name: [sp.firstName, sp.lastName].join(' '),
          value: sp.userId,
        })),
      ),
      publishReplay(1),
      refCount(),
    );

    this.digitalagentsOptions$ = combineLatest([
      this.loggedInUserInfoService.loggedInUserInfo$,
      this.loopOptions$,
    ]).pipe(
      take(1),
      switchMap(([userInfo, loopOptions]) => {
        if (!loopOptions?.forcedFilters?.userId && !loopOptions?.forcedFilters?.marketIds) {
          return this.getPartnerAssignees(userInfo.partnerId);
        }
        return of({
          version: '0',
          data: [],
          requestId: '',
          responseTime: 0,
          statusCode: 0,
        });
      }),
      map((response) => response.data),
      map((data) => {
        const agents = [];
        data.forEach((agent) => {
          agents.push(this.armResultToFilterOption(agent));
        });
        return agents;
      }),
      publishReplay(1),
      refCount(),
    );

    this.salespeopleFilterDisplay$ = combineLatest([
      this.salespeopleOptions$,
      this.selectedSalespeopleUserIds$,
      this.translate.stream('FRONTEND.LOOP.FILTERS.SALESPEOPLE'),
    ]).pipe(
      map(([options, selectedIds, plural]) => {
        const selected = options.filter((sp) => selectedIds.some((id) => id === sp.value));

        if (selected.length > 2) {
          return `${selected.length} ${plural}`;
        } else {
          return selected.map((sp) => sp.name).join(', ');
        }
      }),
    );

    this.digitalagentsFilterDisplay$ = combineLatest([
      this.digitalagentsOptions$,
      this.selectedDigitalAgentsUserIds$,
      this.translate.stream('FRONTEND.LOOP.FILTERS.TASK_MANAGER_USERS'),
    ]).pipe(
      map(([options, selectedIds, plural]) => {
        const selected = options.filter((da) => selectedIds.some((id) => id === da.value));

        if (selected.length > 2) {
          return `${selected.length} ${plural}`;
        } else {
          return selected.map((da) => da.name).join(', ');
        }
      }),
    );

    this.salesTeamOptions$ = this.loggedInUserInfoService.loggedInUserInfo$.pipe(
      take(1),
      switchMap((userInfo) => {
        return this.salesTeamService.list(userInfo.partnerId, userInfo.marketId, '', 5000);
      }),
      catchError((err) => {
        console.error(err);
        return of(null);
      }),
      map((resp) => {
        if (!resp?.results) {
          return [];
        }
        return resp.results.map((team) => ({
          name: team.teamName,
          value: team.groupId,
        }));
      }),
      publishReplay(1),
      refCount(),
    );

    this.salesTeamFilterDisplay$ = combineLatest([this.salesTeamOptions$, this.selectedSalesTeam$]).pipe(
      map(([salesTeamOptions, selectedSalesTeam]) => {
        if (!selectedSalesTeam) {
          return '';
        }
        const team = salesTeamOptions.find((m) => selectedSalesTeam.value === m.value);
        return team.name;
      }),
    );

    this.selectedSalesTeamMemberUserIds$ = this.selectedSalesTeam$.pipe(
      switchMap((selectedSalesTeam) => {
        if (!selectedSalesTeam) {
          return of([]);
        }
        return this.salesTeamService
          .getMembers(selectedSalesTeam.value)
          .pipe(map((salespeople) => (salespeople || []).map((sp) => sp.userId)));
      }),
    );

    this.filteringUserIds$ = combineLatest([
      this.selectedSalespeopleUserIds$,
      this.selectedSalesTeamMemberUserIds$,
      this.selectedDigitalAgentsUserIds$,
      this.loopOptions$,
    ]).pipe(
      map(([selectedSalespeopleUserIds, selectedSalesTeamMemberUserIds, selectedDigitalAgentsUserIds, loopOptions]) => {
        let selectedUserIds = selectedSalespeopleUserIds || [];
        if (loopOptions?.forcedFilters?.userId) {
          selectedUserIds = [loopOptions?.forcedFilters.userId];
        } else if (selectedSalesTeamMemberUserIds?.length > 0) {
          selectedUserIds = selectedUserIds.concat(selectedSalesTeamMemberUserIds);
        }
        if (selectedDigitalAgentsUserIds?.length > 0) {
          selectedUserIds = selectedUserIds.concat(selectedDigitalAgentsUserIds);
        }
        return selectedUserIds;
      }),
    );

    this.channelOptions$ = this.loopOptions$.pipe(
      switchMap((loopOptions) => {
        if (loopOptions?.forcedChannel) {
          return of([]);
        }
        return this.loopService.getChannels();
      }),
      map((channels) => {
        channels = channels.map((c) => ({ name: c, value: c }));
        channels.unshift(ALL_CHANNELS_OPTION);
        return channels;
      }),
      publishReplay(1),
      refCount(),
    );

    this.loopOptions$
      .pipe(
        switchMap((loopOptions) =>
          this.loopService.listPostTypes(
            loopOptions?.forcedChannel ? { channel: loopOptions.forcedChannel } : null,
            '',
            1000,
          ),
        ),
        tap((resp) => this.postTypes$$.next(resp.results.map((postType) => postType))),
        take(1),
      )
      .subscribe();

    this.postTypes$
      .pipe(
        map((postTypes) => {
          const filters = new Map<string, FilterConfig>();
          postTypes.forEach((postType) => {
            (postType.filters || []).forEach((filter) => {
              const filterId = postType.typeId + '::' + filter.value.id;
              filters.set(filterId, {
                id: filterId,
                typeId: postType.typeId,
                title: filter.label,
                options: filter.options.map((o) => ({ name: o.id, value: o.value })),
              });
              this.resourceFiltersSelectedValues.set(
                filterId,
                new BehaviorSubject<ResourceFilterSelectedValues>({ id: filterId, values: [] }),
              );
            });
          });

          this.resourceFiltersMap$$.next(filters);
        }),
        take(1),
      )
      .subscribe();

    this.resourceFilters$ = combineLatest([
      this.postTypes$,
      this.resourceFiltersMap$,
      this.loopOptions$,
      this.selectedChannel$,
      this.channelOptions$,
    ]).pipe(
      map(([postTypes, resourceFiltersMap, loopOptions, selectedChannel, channelOptions]) => {
        if (!loopOptions?.forcedChannel && !selectedChannel && channelOptions.length > 2) {
          // do not show resource filters if multiple channels are being shown (channelOptions includes ALL CHANNELS always)
          return [];
        }
        const postTypeIdFilters = this.getPostTypeIdsForSelectedChannel(postTypes, selectedChannel);
        const results = [];
        for (const v of resourceFiltersMap.values()) {
          if (postTypeIdFilters.some((t) => t === v.typeId)) {
            results.push(v);
          }
        }
        return results;
      }),
    );

    this.dateRangeFilterDisplay$ = this.dateRangeService.getFilter().pipe(
      map((filter) => {
        if (!filter) {
          return '';
        }
        return this.translate.instant(filter.i18nKey);
      }),
    );

    this.initializeSavedFilters();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  getSelectedMarket(): FilterOption {
    return this.selectedMarket$$.getValue();
  }

  setSelectedMarket(market: FilterOption): void {
    this.selectedMarket$$.next(market);
  }

  getSelectedSalespeopleUserIds(): string[] {
    return this.selectedSalespeopleUserIds$$.getValue();
  }

  getSelectedDigitalAgentsUserIds(): string[] {
    return this.selectedDigitalAgentsUserIds$$.getValue();
  }

  setSelectedDigitalAgentsUserIds(userIds: string[]): void {
    this.selectedDigitalAgentsUserIds$$.next(userIds);
  }

  setSelectedSalespeopleUserIds(userIds: string[]): void {
    this.selectedSalespeopleUserIds$$.next(userIds);
  }

  getSelectedSalesTeam(): FilterOption {
    return this.selectedSalesTeam$$.getValue();
  }

  setSelectedSalesTeam(team: FilterOption): void {
    this.selectedSalesTeam$$.next(team);
  }

  setSelectedDateRange(dateRange: ListPostsRequestDateRangeFilterInterface): void {
    this.selectedDateRange$$.next(dateRange);
  }

  getSelectedDateRange(): ListPostsRequestDateRangeFilterInterface {
    return this.selectedDateRange$$.getValue();
  }

  getSelectedresourceFilterValues(filterId: string): string[] {
    const selected$$ = this.resourceFiltersSelectedValues.get(filterId);
    if (selected$$ !== undefined) {
      return selected$$.getValue().values;
    }
    return [];
  }

  setSelectedResourceFilterValues(filterId: string, values: string[]): void {
    const selected$$ = this.resourceFiltersSelectedValues.get(filterId);
    if (selected$$ !== undefined) {
      selected$$.next({ id: filterId, values: values });
    }
  }

  getFilterDisplayForResourceFilter(filterId: string): Observable<string> {
    const options$ = this.resourceFiltersMap$.pipe(
      map((resourceFilters) => resourceFilters.get(filterId)),
      map((resourceFilter) => (resourceFilter ? resourceFilter.options : [])),
    );
    let resourceFiltersSelectedValues$ = of({ id: filterId, values: [] });
    const resourceFiltersSelectedValues$$ = this.resourceFiltersSelectedValues.get(filterId);
    if (resourceFiltersSelectedValues$$ !== undefined) {
      resourceFiltersSelectedValues$ = resourceFiltersSelectedValues$$.asObservable();
    }
    const selectedIds$ = resourceFiltersSelectedValues$.pipe(
      map((resourceFiltersSelectedValues) => resourceFiltersSelectedValues.values),
    );
    return combineLatest([options$, selectedIds$, this.translate.stream('FRONTEND.LOOP.VALUES')]).pipe(
      map(([options, selectedIds, plural]) => {
        const selected = options.filter((sp) => selectedIds.some((id) => id === sp.value));

        if (selected.length > 2) {
          return `${selected.length} ${plural}`;
        } else {
          return selected.map((sp) => sp.name).join(', ');
        }
      }),
    );
  }

  setSelectedChannel(channel: string): void {
    this.selectedChannel$$.next(channel);
    // Reset channel specific filters when changing channels
    [...this.resourceFiltersSelectedValues.values()].forEach((x) => {
      const current = x.getValue();
      x.next({ id: current.id, values: [] });
    });
  }

  getSelectedChannel(): string {
    return this.selectedChannel$$.getValue();
  }

  public initialize(loopOptions: LoopOptions): void {
    this.id = loopOptions?.instanceId || 'default';
    this.compact = loopOptions?.compact;
    if (loopOptions?.pageSize && loopOptions?.pageSize > 0) {
      this.pageSize = loopOptions.pageSize;
    }
    this.loopOptions$$.next(loopOptions);
    const initialFilters$ = this.loadInitialFilters().pipe(publishReplay(1), refCount());
    initialFilters$.pipe(take(1)).subscribe();

    this.subscriptions.push(
      combineLatest([initialFilters$, this.dateRangeService.getFilter()])
        .pipe(
          tap(([, filter]) => {
            if (!filter) {
              return;
            }
            this.selectedDateRangeKey = this.dateRangeService.getKey();
            this.setSelectedDateRange({
              beginRange: filter.start,
              endRange: filter.end,
            });
            this.listPosts(true);
          }),
        )
        .subscribe(),
    );
  }

  applyFilters(serialized: string): Observable<boolean> {
    if (!serialized) {
      this.dateRangeService.setKey(DateFilterOptions.SixMonth);
      return of(true);
    }

    const appliedFilters: AppliedFilters = JSON.parse(serialized);
    if (appliedFilters) {
      const initialFieldsSet$ = combineLatest([
        this.marketOptions$,
        this.salespeopleOptions$,
        this.salesTeamOptions$,
        this.digitalagentsOptions$,
        this.channelOptions$,
      ]).pipe(
        tap(([marketOptions, salespeopleOptions, salesTeamOptions, daOptions, channelOptions]) => {
          if (marketOptions.some((o) => o.value === appliedFilters.market?.value)) {
            this.setSelectedMarket(appliedFilters.market);
          }

          this.setSelectedSalespeopleUserIds(
            (appliedFilters.salespeopleUserIds || []).filter((id) => salespeopleOptions.some((o) => o.value === id)),
          );

          if (salesTeamOptions.some((o) => o.value === appliedFilters.salesTeam?.value)) {
            this.setSelectedSalesTeam(appliedFilters.salesTeam);
          }

          this.setSelectedDigitalAgentsUserIds(
            (appliedFilters.digitalagentsUserIds || []).filter((id) => daOptions.some((o) => o.value === id)),
          );

          if (channelOptions.some((o) => o.value === appliedFilters.channel)) {
            this.setSelectedChannel(appliedFilters.channel);
          }
        }),
      );

      // only apply resource filters after channel has been selected
      return combineLatest([initialFieldsSet$, this.resourceFiltersMap$]).pipe(
        tap(([, resourceFiltersMap]) => {
          (appliedFilters.resourceFilters || []).forEach((f) => {
            const resourceFilter = resourceFiltersMap.get(f.id);
            if (resourceFilter) {
              this.setSelectedResourceFilterValues(
                f.id,
                (f.values || []).filter((id) => resourceFilter.options.some((o) => o.value === id)),
              );
            }
          });
        }),
        // setting dateRange must be last as it triggers listPosts
        tap(() => {
          if (appliedFilters.dateRangeKey === DateFilterOptions.Custom) {
            this.dateRangeService.setAsCustom({
              start: moment(appliedFilters.dateRange.beginRange),
              end: moment(appliedFilters.dateRange.endRange),
            });
          } else {
            this.dateRangeService.setKey(appliedFilters.dateRangeKey);
          }
        }),
        map(() => {
          return true;
        }),
      );
    } else {
      // date range requires a default if nothing else set
      this.dateRangeService.setKey(DateFilterOptions.SixMonth);
      return of(true);
    }
  }

  loadInitialFilters(): Observable<boolean> {
    const serializedFilters = this.getInitialFilters();
    return this.applyFilters(serializedFilters);
  }

  listPosts(replace: boolean): void {
    if (replace) {
      this.nextCursor = '';
      this.posts$$.next([]);
    }
    this.loading$$.next(true);
    combineLatest([
      this.partnerId$,
      this.filteringMarketIds$,
      this.filteringUserIds$,
      this.postTypes$,
      this.selectedChannel$,
      this.selectedDateRange$,
    ])
      .pipe(
        take(1),
        switchMap(([partnerId, marketIds, userIds, postTypes, selectedChannel, dateRange]) => {
          const resourceFiltersSelectedValues = [...this.resourceFiltersSelectedValues.values()].map((v) =>
            v.getValue(),
          );
          const resourceFilters: string[] = [];
          resourceFiltersSelectedValues.forEach((selectedValues) =>
            selectedValues.values.forEach((value) => {
              resourceFilters.push(selectedValues.id + '::' + value);
            }),
          );

          const request: ListPostsRequestInterface = {
            partnerId: partnerId,
            marketIds: marketIds,
            userIds: userIds,
            resourceFilters: resourceFilters,
            postTypeIds: this.getPostTypeIdsForSelectedChannel(postTypes, selectedChannel),
            dateRange: dateRange,
            pagingOptions: {
              pageSize: this.pageSize,
              cursor: this.nextCursor,
            },
            renderOption: this.compact ? RenderOption.RENDER_OPTION_COMPACT : RenderOption.RENDER_OPTION_FULL,
          };
          this.saveFilter(MAIN_FILTER);
          return this.loopService.listPosts(request);
        }),
        finalize(() => this.loading$$.next(false)),
      )
      .subscribe(
        (resp: ListPostsResponseInterface) => {
          this.nextCursor = resp.pagingMetadata.nextCursor;
          if (replace) {
            this.posts$$.next(resp.posts || []);
          } else {
            if ((resp.posts || []).length > 0) {
              this.posts$$.next(this.posts$$.value.concat(resp.posts));
            }
          }
          this.hasMore$$.next(resp.pagingMetadata.hasMore);
        },
        (error) => {
          console.error(error);
          this.alertService.openErrorSnack('FRONTEND.LOOP.ERRORS.ERROR_LOADING_POSTS');
        },
      );
  }

  nextPage(): void {
    if (!this.loading$$.getValue()) {
      this.listPosts(false);
    }
  }

  getPostTypeIdsForSelectedChannel(postTypes: PostType[], selectedChannel: string): string[] {
    const results = [];
    postTypes.forEach((pt) => {
      if (!selectedChannel || selectedChannel === pt.channel) {
        results.push(pt.typeId);
      }
    });
    return results;
  }

  getLoopFilterId(): string {
    return THE_LOOP + this.id;
  }

  getInitialFilters(): string {
    const params = this.route.snapshot.queryParams;
    if (params[this.getLoopFilterId()]) {
      return decodeURIComponent(params[this.getLoopFilterId()]);
    }

    const localStorageFilters = this.localStorageService.get(this.getLoopFilterId());
    if (!localStorageFilters) {
      return '';
    }

    const currentFilters: SavedFilter[] = JSON.parse(localStorageFilters);
    const mainFilter = currentFilters.find((filter) => filter.name === MAIN_FILTER);
    if (mainFilter) {
      return mainFilter.serializedFields;
    }

    return '';
  }

  initializeSavedFilters(): void {
    const savedFiltersStorage = this.localStorageService.get(this.getLoopFilterId());

    this.savedFilters$ = this.savedFilters$$.asObservable().pipe(map((s) => s.filter((f) => f.name !== MAIN_FILTER)));

    if (!savedFiltersStorage) {
      return;
    }

    const savedFilters = JSON.parse(savedFiltersStorage);
    this.savedFilters$$.next(savedFilters);
  }

  private applyFiltersToURL(filter: SavedFilter): void {
    if (this.compact) {
      return;
    }
    const p = {};
    p[this.getLoopFilterId()] = filter.serializedFields;
    this.urlParamService.applyParams(p);
  }

  private saveFiltersToStorage(filters: SavedFilter[]): void {
    this.localStorageService.set(this.getLoopFilterId(), JSON.stringify(filters));
    this.savedFilters$$.next(filters);
  }

  selectFilter(filter: SavedFilter): void {
    this.applyFiltersToURL(filter);
    // TODO: Fix loading resources and multiple loads of saved filters
    this.applyFilters(filter.serializedFields).pipe(take(1)).subscribe();
  }

  saveFilter(filter: string): void {
    // Cannot create filter with empty name
    if (!filter) {
      this.alertService.openErrorSnack('FRONTEND.LOOP.ERRORS.CREATE_EMPTY_FILTER_ERROR');
      return;
    }

    const resourceFiltersSelectedValues = [...this.resourceFiltersSelectedValues.values()].map((v) => v.getValue());
    const appliedFilters: AppliedFilters = {
      market: this.getSelectedMarket(),
      salespeopleUserIds: this.getSelectedSalespeopleUserIds(),
      digitalagentsUserIds: this.getSelectedDigitalAgentsUserIds(),
      salesTeam: this.getSelectedSalesTeam(),
      resourceFilters: resourceFiltersSelectedValues,
      dateRangeKey: this.selectedDateRangeKey,
      dateRange: this.getSelectedDateRange(),
      channel: this.getSelectedChannel(),
    };

    const serializedFilters = JSON.stringify(appliedFilters);
    const newFilter: SavedFilter = {
      name: filter,
      serializedFields: serializedFilters,
      expiry: null,
    };

    const currentFilters = this.savedFilters$$.getValue();
    const foundFilter = currentFilters.findIndex((f) => f.name === newFilter.name);
    if (foundFilter !== -1) {
      currentFilters[foundFilter] = newFilter;
    } else {
      currentFilters.push(newFilter);
    }

    this.applyFiltersToURL(newFilter);
    this.saveFiltersToStorage(currentFilters);
  }

  removeFilter(filter: string): void {
    const currentFilters = this.savedFilters$$.getValue();
    const foundFilter = currentFilters.findIndex((f) => f.name === filter);

    // If not found, nothing to remove
    if (foundFilter === -1) {
      this.alertService.openErrorSnack('FRONTEND.LOOP.ERRORS.REMOVE_FILTER_ERROR');
      return;
    }

    currentFilters.splice(foundFilter, 1);

    this.saveFiltersToStorage(currentFilters);
  }

  private armResultToFilterOption(result: DigitalAgentApiInterface): FilterOption {
    let firstName = '';
    let lastName = '';
    const filterUser: FilterOption = {
      name: '',
      value: result._user_id,
    };
    if (result.first_name) {
      firstName = result.first_name;
    }
    if (result.last_name) {
      lastName = result.last_name;
    }
    filterUser.name = !!firstName || !!lastName ? [firstName, lastName].join(' ') : result.email;
    return filterUser;
  }

  getHost(): string {
    const env = this.environmentService.getEnvironment();
    switch (env) {
      case Environment.DEMO:
        return 'https://arm-demo.appspot.com/';
      case Environment.PROD:
        return 'https://task-manager.biz/';
      default:
        return 'https://arm-demo.appspot.com/';
    }
  }

  getPartnerAssignees(partnerId: string): Observable<ArmApiResponse<DigitalAgentApiInterface[]>> {
    const apiOptions = {
      withCredentials: true,
      params: new HttpParams().set('partnerId', partnerId),
    };

    return this.http.get<ArmApiResponse<DigitalAgentApiInterface[]>>(
      this.getHost() + 'internalApi/v3/user/assignees/',
      apiOptions,
    );
  }
}
